import { useQuery, type QueryClient } from "@tanstack/react-query";
import { randomUUID } from "expo-crypto";
import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";
import type {
  Collection,
  Plant,
  PlantCareSchedule,
  PlantJournalEntry,
  PlantPhoto,
} from "~/api/types";
import { useAuth } from "~/hooks/useAuth";

export type {
  Collection,
  Plant,
  PlantCareSchedule,
  PlantJournalEntry,
  PlantPhoto,
};

/** Rows are created locally with a client id so a plant added on a plane keeps
 * the same identity once it reaches the API. */
export const generateId = () => randomUUID();

export type EntityName =
  | "collections"
  | "plants"
  | "plant_photos"
  | "plant_care_schedules"
  | "plant_journal_entries";

/** Query keys are scoped to the owner — the cache is persisted and a device can
 * be shared, so one user's rows must never restore under another's key. */
export const flKey = (entity: EntityName, userId: string) =>
  [entity, userId] as const;

// --- request bodies -------------------------------------------------------
// Rows carry server-managed columns (owner_id, timestamps, display_name) the API
// ignores, so each write sends only the fields the endpoint accepts.

export const collectionBody = (row: Partial<Collection>) => ({
  name: row.name as string,
  description: row.description,
  visibility: row.visibility,
  cover_photo_path: row.cover_photo_path,
  accent_color: row.accent_color,
  sort_order: row.sort_order,
  map_kind: row.map_kind,
  map_style: row.map_style,
  map_center_lat: row.map_center_lat,
  map_center_lng: row.map_center_lng,
  map_zoom: row.map_zoom,
  custom_map_path: row.custom_map_path,
  custom_map_width: row.custom_map_width,
  custom_map_height: row.custom_map_height,
  deleted: row.deleted,
});

export const plantBody = (row: Partial<Plant>) => ({
  collection_id: row.collection_id as string,
  taxon_id: row.taxon_id,
  nickname: row.nickname,
  status: row.status,
  placement: row.placement,
  acquisition: row.acquisition,
  quantity: row.quantity,
  acquired_on: row.acquired_on,
  acquired_from: row.acquired_from,
  planted_on: row.planted_on,
  pot_size_mm: row.pot_size_mm,
  notes: row.notes,
  tags: row.tags,
  cover_photo_path: row.cover_photo_path,
  map_lat: row.map_lat,
  map_lng: row.map_lng,
  map_x: row.map_x,
  map_y: row.map_y,
  deleted: row.deleted,
});

export const photoBody = (row: Partial<PlantPhoto>) => ({
  plant_id: row.plant_id as string,
  storage_path: row.storage_path as string,
  caption: row.caption,
  taken_at: row.taken_at,
  sort_order: row.sort_order,
  deleted: row.deleted,
});

export const scheduleBody = (row: Partial<PlantCareSchedule>) => ({
  plant_id: row.plant_id as string,
  task_kind: row.task_kind as PlantCareSchedule["task_kind"],
  interval_days: row.interval_days as number,
  next_due_at: row.next_due_at as string,
  last_completed_at: row.last_completed_at,
  enabled: row.enabled,
  deleted: row.deleted,
});

export const journalBody = (row: Partial<PlantJournalEntry>) => ({
  plant_id: row.plant_id as string,
  kind: row.kind,
  body: row.body,
  occurred_at: row.occurred_at,
  deleted: row.deleted,
});

/** Creates or replaces one row by its client id (idempotent, safe to replay). */
export async function putRow(entity: EntityName, row: Record<string, unknown>) {
  const path = { id: row.id as string };
  switch (entity) {
    case "collections":
      return unwrap(
        api.putCollection({
          path,
          body: collectionBody(row as Partial<Collection>),
        }),
      );
    case "plants":
      return unwrap(
        api.putPlant({ path, body: plantBody(row as Partial<Plant>) }),
      );
    case "plant_photos":
      return unwrap(
        api.putPlantPhoto({
          path,
          body: photoBody(row as Partial<PlantPhoto>),
        }),
      );
    case "plant_care_schedules":
      return unwrap(
        api.putPlantCareSchedule({
          path,
          body: scheduleBody(row as Partial<PlantCareSchedule>),
        }),
      );
    case "plant_journal_entries":
      return unwrap(
        api.putPlantJournalEntry({
          path,
          body: journalBody(row as Partial<PlantJournalEntry>),
        }),
      );
  }
}

/** Changes some fields of an existing row. */
export async function patchRow(
  entity: EntityName,
  id: string,
  fields: Record<string, unknown>,
) {
  const path = { id };
  switch (entity) {
    case "collections":
      return unwrap(
        api.patchCollection({
          path,
          body: collectionBody(fields as Partial<Collection>),
        }),
      );
    case "plants":
      return unwrap(
        api.patchPlant({ path, body: plantBody(fields as Partial<Plant>) }),
      );
    case "plant_photos":
      return unwrap(
        api.patchPlantPhoto({
          path,
          body: photoBody(fields as Partial<PlantPhoto>),
        }),
      );
    case "plant_care_schedules":
      return unwrap(
        api.patchPlantCareSchedule({
          path,
          body: scheduleBody(fields as Partial<PlantCareSchedule>),
        }),
      );
    case "plant_journal_entries":
      return unwrap(
        api.patchPlantJournalEntry({
          path,
          body: journalBody(fields as Partial<PlantJournalEntry>),
        }),
      );
  }
}

/** Soft-deletes one row. The API trashes whatever hangs off it (a plant's
 * photos, schedules and journal; a collection's plants). */
export async function removeRow(entity: EntityName, id: string) {
  const path = { id };
  switch (entity) {
    case "collections":
      return unwrap(api.deleteCollection({ path }));
    case "plants":
      return unwrap(api.deletePlant({ path }));
    case "plant_photos":
      return unwrap(api.deletePlantPhoto({ path }));
    case "plant_care_schedules":
      return unwrap(api.deletePlantCareSchedule({ path }));
    case "plant_journal_entries":
      return unwrap(api.deletePlantJournalEntry({ path }));
  }
}

async function fetchOwned<T>(entity: EntityName): Promise<T[]> {
  switch (entity) {
    case "collections":
      return (await unwrap(api.listCollections())).items as T[];
    case "plants":
      return (await unwrap(api.listPlants())).items as T[];
    case "plant_photos":
      return (await unwrap(api.listPlantPhotos())).items as T[];
    case "plant_care_schedules":
      return (await unwrap(api.listPlantCareSchedules())).items as T[];
    case "plant_journal_entries":
      return (await unwrap(api.listPlantJournalEntries())).items as T[];
  }
}

/** The account's collections straight from the API, for callers (like onboarding) that must not trust a query that may not have loaded yet. */
export const fetchCollections = () => fetchOwned<Collection>("collections");

function useOwnedQuery<T>(entity: EntityName) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  return useQuery({
    queryKey: flKey(entity, userId ?? ""),
    queryFn: () => fetchOwned<T>(entity),
    enabled: Boolean(userId),
  });
}

export const useCollectionsData = () =>
  useOwnedQuery<Collection>("collections");
export const usePlantsData = () => useOwnedQuery<Plant>("plants");
export const usePlantPhotosData = () =>
  useOwnedQuery<PlantPhoto>("plant_photos");
export const useCareSchedulesData = () =>
  useOwnedQuery<PlantCareSchedule>("plant_care_schedules");
export const useJournalEntriesData = () =>
  useOwnedQuery<PlantJournalEntry>("plant_journal_entries");

/** Owner id for the current session — used by the action hooks to tag mutation
 * variables so the offline queue can target the right cache key. */
export function useOwnerId() {
  const { user } = useAuth();
  return user?.id ?? null;
}

// --- Optimistic cache helpers, shared by every mutation default ----------

type WithId = { id: string };

export function cacheUpsert<T extends WithId>(
  queryClient: QueryClient,
  entity: EntityName,
  userId: string,
  row: T,
) {
  queryClient.setQueryData<T[]>(flKey(entity, userId), (current) => {
    const list = current ?? [];
    const index = list.findIndex((item) => item.id === row.id);
    if (index === -1) return [...list, row];
    const next = list.slice();
    next[index] = { ...next[index], ...row };
    return next;
  });
}

export function cachePatch<T extends WithId>(
  queryClient: QueryClient,
  entity: EntityName,
  userId: string,
  id: string,
  patch: Partial<T>,
) {
  queryClient.setQueryData<T[]>(flKey(entity, userId), (current) =>
    (current ?? []).map((item) =>
      item.id === id ? { ...item, ...patch } : item,
    ),
  );
}

export function cacheRemove<T extends WithId>(
  queryClient: QueryClient,
  entity: EntityName,
  userId: string,
  predicate: (row: T) => boolean,
) {
  queryClient.setQueryData<T[]>(flKey(entity, userId), (current) =>
    (current ?? []).filter((item) => !predicate(item)),
  );
}

export function cacheSnapshot(
  queryClient: QueryClient,
  userId: string,
  entities: readonly EntityName[] = OWNED_ENTITIES,
) {
  const all = {
    collections: queryClient.getQueryData(flKey("collections", userId)),
    plants: queryClient.getQueryData(flKey("plants", userId)),
    plant_photos: queryClient.getQueryData(flKey("plant_photos", userId)),
    plant_care_schedules: queryClient.getQueryData(
      flKey("plant_care_schedules", userId),
    ),
    plant_journal_entries: queryClient.getQueryData(
      flKey("plant_journal_entries", userId),
    ),
  };
  return Object.fromEntries(
    entities.map((entity) => [entity, all[entity as keyof typeof all]]),
  ) as Partial<typeof all>;
}

export function cacheRestore(
  queryClient: QueryClient,
  userId: string,
  snapshot: ReturnType<typeof cacheSnapshot>,
) {
  for (const [entity, data] of Object.entries(snapshot)) {
    if (data !== undefined) {
      queryClient.setQueryData(flKey(entity as EntityName, userId), data);
    }
  }
}

const OWNED_ENTITIES = [
  "collections",
  "plants",
  "plant_photos",
  "plant_care_schedules",
  "plant_journal_entries",
] as const;

export function invalidateOwned(
  queryClient: QueryClient,
  userId: string,
  entities: readonly EntityName[] = OWNED_ENTITIES,
) {
  for (const entity of entities) {
    void queryClient.invalidateQueries({ queryKey: flKey(entity, userId) });
  }
}
